import React, { useEffect, useState } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { apiPost } from '../utils/api';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const BillingDashboardView = ({ email }) => {
  return (
    <Elements stripe={stripePromise}>
      <BillingDashboardContent email={email} />
    </Elements>
  );
};

const BillingDashboardContent = ({ email }) => {
  // State variables for payment methods and billing
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [billingInfo, setBillingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('overview'); // Default tab
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Stripe setup
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    fetchAllBillingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllBillingData = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      // Fetch payment methods and invoices
      const userId = sessionStorage.getItem('userId') || email;
      const paymentResponse = await apiPost('/get-payment-methods', { userId });

      setPaymentMethods(paymentResponse.paymentMethods || []);
      setDefaultPaymentMethod(paymentResponse.defaultPaymentMethodId || '');
      setInvoices(paymentResponse.invoices || []);
      
      // Fetch billing usage info
      const billingResponse = await apiPost('/get-user-billing-info', { userId });
      setBillingInfo(billingResponse);

    } catch (err) {
      console.error('Error fetching billing data:', err);
      setErrorMessage('Failed to load billing data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
    if (!window.confirm('Are you sure you want to set this as your default payment method?')) {
      return;
    }

    try {
      setIsUpdating(true);
      const userId = sessionStorage.getItem('userId') || email;
      
      const response = await apiPost('/update-default-payment-method', {
        userId, 
        newPaymentMethodId: paymentMethodId
      });

      if (response.success) {
        setDefaultPaymentMethod(response.newDefault);
        setErrorMessage('');
      }
    } catch (err) {
      console.error('Error updating default payment method:', err);
      setErrorMessage('Could not update default payment method. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddPaymentMethod = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      setErrorMessage('Stripe is still loading. Please wait.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setErrorMessage('Please enter card details.');
      return;
    }

    try {
      setIsSubmitting(true);;
      setErrorMessage('');
      
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        throw new Error(error.message);
      }

      const userId = sessionStorage.getItem('userId') || email;
      await apiPost('/add-payment-method', {
        userId, 
        paymentMethodId: paymentMethod.id
      });

      // Clear the card element
      cardElement.clear();
      
      // Hide the form after adding
      setIsSubmitting(false);
      
      // Refresh payment methods
      await fetchAllBillingData();
      
    } catch (err) {
      console.error('Error adding payment method:', err);
      setErrorMessage(err.message || 'Could not add payment method. Please try again.');
      setIsAdding(false);
    }
  };

  const handleRemovePaymentMethod = async (paymentMethodId) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    try {
      setIsUpdating(true);
      
      await apiPost('/remove-payment-method', { paymentMethodId });
      
      // Refresh data
      await fetchAllBillingData();
      setErrorMessage('');
    } catch (err) {
      console.error('Error removing payment method:', err);
      setErrorMessage('Could not remove payment method. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You will still have access until the end of your current billing cycle.")) {
      return;
    }

    try {
      setIsUpdating(true);
      const userId = sessionStorage.getItem('userId') || email;
      
      const response = await apiPost('/cancel-subscription', { userId });

      if (response.success) {
        // Update local state
        if (billingInfo && billingInfo.subscription) {
          setBillingInfo({
            ...billingInfo,
            subscription: {
              ...billingInfo.subscription,
              cancelAtPeriodEnd: true
            }
          });
        }
        
        setErrorMessage('');
        setMessage('Subscription will be cancelled at the end of the current billing period.');
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setErrorMessage('Could not cancel subscription. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleResumeSubscription = async () => {
    try {
      setIsUpdating(true);
      const userId = sessionStorage.getItem('userId') || email;
      
      const response = await apiPost('/resume-subscription', { userId });

      if (response.success) {
        // Update local state
        if (billingInfo && billingInfo.subscription) {
          setBillingInfo({
            ...billingInfo,
            subscription: {
              ...billingInfo.subscription,
              cancelAtPeriodEnd: false
            }
          });
        }
        
        setErrorMessage('');
        setMessage('Your subscription has been resumed.');
      }
    } catch (err) {
      console.error('Error resuming subscription:', err);
      setErrorMessage('Could not resume subscription. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="billing-loading">
        Loading billing data...
      </div>
    );
  }

  return (
    <div className="billing-dashboard-main">
      <div className="billing-dashboard-header">
        <h2 className="billing-dashboard-title">Billing Dashboard</h2>
      </div>
      
      {errorMessage && (
        <div className="billing-message error">
          {errorMessage}
        </div>
      )}

      {message && (
        <div className="billing-message success">
          {message}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="billing-tab-nav">
        <nav className="billing-tab-nav-list">
          <button
            onClick={() => setActiveTab('overview')}
            className={`billing-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('payment-methods')}
            className={`billing-tab-btn ${activeTab === 'payment-methods' ? 'active' : ''}`}
          >
            Payment Methods
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`billing-tab-btn ${activeTab === 'invoices' ? 'active' : ''}`}
          >
            Invoices & History
          </button>
        </nav>
      </div>

      {/* Overview Tab Content */}
      {activeTab === 'overview' && billingInfo && billingInfo.currentCycle && (
        <div className="billing-tab-content">
          {/* Quick Stats Cards */}
          <div className="billing-stats-grid">
            <div className="billing-stat-card">
              <h3 className="billing-stat-label">Subscription</h3>
              <p className="billing-stat-value">${billingInfo.currentCycle.baseSubscriptionFee.toFixed(2)}/mo</p>
              <p className="billing-stat-subtitle">
                {billingInfo.subscription?.trialActive ? (
                  <>First charge: {formatDate(billingInfo.subscription.trialEnd)}</>
                ) : billingInfo.subscription?.currentPeriodEnd ? (
                  <>Next billing: {formatDate(billingInfo.subscription.currentPeriodEnd)}</>
                ) : (
                  <>Next billing: {formatDate(billingInfo.currentCycle.endDate)}</>
                )}
              </p>
            </div>
            <div className="billing-stat-card">
              <h3 className="billing-stat-label">Episodes This Cycle</h3>
              <p className="billing-stat-value">{billingInfo.currentCycle.episodesProcessed}</p>
              <p className="billing-stat-subtitle">
                {12 - billingInfo.currentCycle.episodesProcessed} remaining
              </p>
            </div>
            <div className="billing-stat-card">
              <h3 className="billing-stat-label">Additional Charges</h3>
              <p className="billing-stat-value">${billingInfo.currentCycle.additionalCharges.toFixed(2)}</p>
              <p className="billing-stat-subtitle">
                From {billingInfo.currentCycle.additionalEpisodesCharged} extra episodes
              </p>
            </div>
          </div>

          {/* Subscription Management */}
          {billingInfo && billingInfo.subscription && (  // ✅ This line already exists
            <div className="billing-table-container">
              <div className="billing-table-header">
                <h3 className="billing-table-title">Subscription Management</h3>
              </div>
              <div className="subscription-management-content">
                <p className="subscription-status">
                  Status: <strong>{billingInfo.subscription.status}</strong>
                </p>

                {/* Trial notice */}
                {billingInfo.subscription.trialActive && (
                  <div className="trial-active-notice" style={{
                    padding: 'var(--space-4)',
                    backgroundColor: '#e7f3ff',
                    border: '1px solid #2196F3',
                    borderRadius: 'var(--radius)',
                    marginTop: 'var(--space-4)',
                    marginBottom: 'var(--space-4)'
                  }}>
                    <strong>🎉 Free Trial Active</strong>
                    <p style={{ marginTop: 'var(--space-2)', marginBottom: 0 }}>
                      Your trial ends on {formatDate(billingInfo.subscription.trialEnd)}. 
                      Your first payment of ${billingInfo.currentCycle.baseSubscriptionFee.toFixed(2)} will be charged on that date.
                    </p>
                  </div>
                )}
                
                {billingInfo.subscription.cancelAtPeriodEnd && (
                  <div className="subscription-cancellation-notice">
                    ⚠️ Your subscription is set to cancel at the end of the current billing period. 
                    You will still have access until {formatDate(billingInfo.subscription.currentPeriodEnd)}.
                  </div>
                )}
                
                <div className="subscription-actions">
                  {!billingInfo.subscription.cancelAtPeriodEnd ? (
                    <button 
                      onClick={handleCancelSubscription}
                      disabled={isUpdating}
                      className="billing-btn-secondary"
                    >
                      {isUpdating ? "Processing..." : "Cancel Subscription"}
                    </button>
                  ) : (
                    <button 
                      onClick={handleResumeSubscription}
                      disabled={isUpdating}
                      className="billing-btn-primary"
                    >
                      {isUpdating ? "Processing..." : "Resume Subscription"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Billing History */}
          {billingInfo && billingInfo.billingHistory && billingInfo.billingHistory.length > 0 && (
            <div className="billing-table-container">
              <div className="billing-table-header">
                <h3 className="billing-table-title">Billing History</h3>
              </div>
              <div className="billing-table-wrapper">
                <table className="billing-table">
                  <thead>
                    <tr>
                      <th>Billing Period</th>
                      <th className="center">Base Episodes</th>
                      <th className="center">Additional Episodes</th>
                      <th className="center">Base Fee</th>
                      <th className="center">Additional Charges</th>
                      <th className="right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingInfo.billingHistory.map((cycle) => (
                      <tr key={cycle.cycleId}>
                        <td>{formatDate(cycle.startDate)} - {formatDate(cycle.endDate)}</td>
                        <td className="center">12</td>
                        <td className="center">{cycle.additionalEpisodesCharged}</td>
                        <td className="center">${cycle.baseSubscriptionFee.toFixed(2)}</td>
                        <td className="center">${cycle.additionalCharges.toFixed(2)}</td>
                        <td className="right">${cycle.totalBilled.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Methods Tab Content */}
      {activeTab === 'payment-methods' && (
        <div className="billing-table-container">
          <div className="billing-table-header">
            <h3 className="billing-table-title">Saved Payment Methods</h3>
          </div>
          <div className="payment-methods-content">
            {paymentMethods.length === 0 ? (
              <div className="billing-empty-state">
                <p>No payment methods found.</p>
              </div>
            ) : (
              <div className="payment-methods-list">
                {paymentMethods.map(pm => (
                  <div key={pm.id} className="payment-method-item">
                    <div className="payment-method-info">
                      <div className="payment-method-details">
                        <div className="payment-method-brand">
                          {pm.card.brand === 'visa' && '💳 Visa'}
                          {pm.card.brand === 'mastercard' && '💳 Mastercard'}
                          {pm.card.brand === 'amex' && '💳 American Express'}
                          {pm.card.brand === 'discover' && '💳 Discover'}
                          {!['visa', 'mastercard', 'amex', 'discover'].includes(pm.card.brand) && '💳 Card'}
                        </div>
                        <div className="payment-method-number">
                          ending in {pm.card.last4} 
                          <span className="payment-method-expiry">
                            (expires {pm.card.exp_month}/{pm.card.exp_year})
                          </span>
                        </div>
                      </div>
                      
                      {pm.id === defaultPaymentMethod && (
                        <span className="payment-method-default-badge">
                          Default
                        </span>
                      )}
                    </div>
                    
                    <div className="payment-method-actions">
                      {pm.id !== defaultPaymentMethod && (
                        <button 
                          onClick={() => handleSetDefaultPaymentMethod(pm.id)}
                          disabled={isUpdating}
                          className="billing-btn-secondary"
                        >
                          Set Default
                        </button>
                      )}
                      
                      <button 
                        onClick={() => handleRemovePaymentMethod(pm.id)}
                        disabled={isUpdating || pm.id === defaultPaymentMethod}
                        className={`billing-btn-secondary ${pm.id === defaultPaymentMethod ? 'disabled' : ''}`}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="add-payment-method-section">
              <button 
                onClick={() => setIsAdding(!isAdding)}
                className="billing-btn-primary"
              >
                {isAdding ? 'Cancel' : 'Add New Payment Method'}
              </button>
              
              {isAdding && (
                <form onSubmit={handleAddPaymentMethod} className="add-payment-form">
                  <h4 className="add-payment-form-title">Add New Card</h4>
                  <div className="card-element-wrapper">
                    <CardElement />
                  </div>
                  <button 
                    type="submit" 
                    disabled={!stripe || isSubmitting}
                    className="billing-btn-primary"
                  >
                    {isSubmitting ? 'Adding...' : 'Save Card'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Invoices & History Tab Content */}
      {activeTab === 'invoices' && (
        <div className="billing-table-container">
          <div className="billing-table-header">
            <h3 className="billing-table-title">Recent Invoices</h3>
          </div>
          
          {invoices.length === 0 ? (
            <div className="billing-empty-state">
              <p>No invoices found.</p>
            </div>
          ) : (
            <div className="billing-table-wrapper">
              <table className="billing-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th className="right">Amount</th>
                    <th className="right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id}>
                      <td>{new Date(inv.created * 1000).toLocaleDateString()}</td>
                      <td>
                        {inv.lines.data.length > 0 
                          ? inv.lines.data[0].description || 'Monthly subscription'
                          : 'Monthly subscription'}
                      </td>
                      <td>
                        <span className={`billing-status-badge ${inv.status}`}>
                          {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                        </span>
                      </td>
                      <td className="right">${(inv.amount_due / 100).toFixed(2)}</td>
                      <td className="right">
                        {inv.hosted_invoice_url && (
                          <a 
                            href={inv.hosted_invoice_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="billing-btn-secondary"
                          >
                            View
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Footer with support info */}
      <div className="billing-footer">
        <p>
          Need help with your billing? Contact us at <a href="mailto:support@smack.fm">support@smack.fm</a>
        </p>
      </div>
    </div>
  );
};

export default BillingDashboardView;